import type { SiteSummary } from "@plenum/shared/api";
import { Link } from "@tanstack/react-router";
import { fmtCoord, fmtInt, fmtMw } from "../lib/format";
import {
  CONTACT_LABEL,
  COOLING_LABEL,
  FRAME_LABEL,
  label,
  SECTOR_LABEL,
  SEGMENT_LABEL,
  SITE_TYPE_LABEL,
} from "../lib/tiers";
import { TierBadge } from "./Badge";
import type { Column } from "./DataTable";

/** Shared column definitions for the table view and list pages. */
export const SITE_COLUMNS: Column<SiteSummary>[] = [
  {
    key: "name",
    label: "Site",
    sortKey: "name",
    default: true,
    render: (s) => (
      <Link
        to="/sites/$id"
        params={{ id: String(s.id) }}
        className="font-medium"
        data-testid="site-link"
      >
        {s.name}
      </Link>
    ),
  },
  { key: "state", label: "State", sortKey: "state", default: true, render: (s) => s.state },
  { key: "city", label: "City", render: (s) => s.city ?? "—" },
  {
    key: "operator",
    label: "Operator",
    sortKey: "operator",
    default: true,
    render: (s) => s.operator?.name ?? "—",
  },
  {
    key: "tier",
    label: "Tier",
    sortKey: "score",
    default: true,
    title: "Prospect tier and score",
    render: (s) => <TierBadge tier={s.tier} score={s.score} />,
  },
  {
    key: "units_f",
    label: "Units",
    sortKey: "units",
    align: "r",
    default: true,
    title: "Units matching the filter",
    render: (s) => <span className="num">{fmtInt(s.units_f)}</span>,
  },
  {
    key: "mw_f",
    label: "MW",
    sortKey: "mw",
    align: "r",
    default: true,
    title: "Nameplate MW of units matching the filter",
    render: (s) => <span className="num">{fmtMw(s.mw_f)}</span>,
  },
  {
    key: "core_f",
    label: "Core",
    align: "r",
    title: "Units in the 40 to 90 MW core band",
    render: (s) => <span className="num">{fmtInt(s.core_f)}</span>,
  },
  { key: "frame", label: "Frame", default: true, render: (s) => label(FRAME_LABEL, s.frame_class) },
  {
    key: "cooling",
    label: "Inlet cooling",
    default: true,
    render: (s) => label(COOLING_LABEL, s.inlet_cooling),
  },
  {
    key: "contact",
    label: "Contact",
    default: true,
    render: (s) => label(CONTACT_LABEL, s.contact_status),
  },
  { key: "site_type", label: "Type", render: (s) => label(SITE_TYPE_LABEL, s.site_type) },
  {
    key: "sector",
    label: "Sector",
    render: (s) =>
      s.sector_id ? label(SECTOR_LABEL, String(s.sector_id)) : (s.sector_label ?? "—"),
  },
  {
    key: "ba",
    label: "BA",
    title: "Balancing authority",
    render: (s) => s.balancing_authority ?? "—",
  },
  {
    key: "year",
    label: "Since",
    sortKey: "year",
    align: "r",
    render: (s) => <span className="num">{s.first_operating_year ?? "—"}</span>,
  },
  { key: "segment", label: "Segment", render: (s) => label(SEGMENT_LABEL, s.segment) },
  { key: "status", label: "Status", render: (s) => s.status },
  {
    key: "mwh",
    label: "Est. MWh/yr",
    align: "r",
    render: (s) => (
      <span className="num">{s.est_mwh_yr === null ? "—" : fmtInt(s.est_mwh_yr)}</span>
    ),
  },
  {
    key: "coords",
    label: "Coordinates",
    render: (s) => <span className="num">{fmtCoord(s.lat, s.lon)}</span>,
  },
  {
    key: "eia",
    label: "EIA id",
    align: "r",
    render: (s) => <span className="num">{s.eia_plant_id ?? "—"}</span>,
  },
  {
    key: "map",
    label: "",
    render: (s) => (
      <Link
        to="/"
        search={{ site: s.id, c: [s.lon, s.lat], z: 11 }}
        className="text-[11px]"
        aria-label={`Show ${s.name} on the map`}
      >
        map
      </Link>
    ),
  },
];

export const DEFAULT_SITE_COLUMNS = SITE_COLUMNS.filter((c) => c.default)
  .map((c) => c.key)
  .concat(["map"]);
